import { useState, useMemo, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useJobs } from '@/hooks/useJobs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, Search, X } from 'lucide-react';
import { JobsTableView } from '@/components/jobs/JobsTableView';
import { isDeclinedByTech, jobBoardStatus } from '@/utils/techProgress';
import { JobsDashboard, type JobsAttentionKind } from '@/components/jobs/JobsDashboard';
import { JobQuickViewSheet } from '@/components/jobs/JobQuickViewSheet';
import { AdminOnly } from '@/components/AdminOnly';
import { useTechs, useMetros } from '@/hooks/useReferenceData';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useQuickViewNavigation } from '@/hooks/useQuickViewNavigation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { JobsHeader } from '@/components/jobs/JobsHeader';
import { formatInTz } from '@/utils/timezone';

type JobTab = 'ongoing' | 'scheduled' | 'on_hold' | 'quote_sent' | 'completed' | 'cancelled';

const TAB_TO_STATUS: Record<JobTab, string> = {
  ongoing: 'in_progress',
  scheduled: 'scheduled',
  on_hold: 'on_hold',
  quote_sent: 'quote_sent',
  completed: 'completed',
  cancelled: 'cancelled',
};

const TAB_DEFS: { value: JobTab; label: string }[] = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'scheduled', label: 'Upcoming' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'quote_sent', label: 'Quote sent' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ITEMS_PER_PAGE = 20;

type Appointment = { scheduled_date: string | null; scheduled_time_start?: string | null };

/** Soonest appointment first; jobs with no date go to the end. */
function byAppointment(a: Appointment, b: Appointment) {
  if (!a.scheduled_date || !b.scheduled_date) return a.scheduled_date ? -1 : b.scheduled_date ? 1 : 0;
  const d = new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime();
  if (d !== 0) return d;
  return (a.scheduled_time_start ?? '99').localeCompare(b.scheduled_time_start ?? '99');
}

export default function Jobs() {
  const { now, startOfDay, endOfDay, timezone } = useTimezoneUtils();
  
  const [activeTab, setActiveTab] = useState<JobTab>('ongoing');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);
  const effectiveSearch = debouncedSearch.length >= 2 ? debouncedSearch : '';

  // Date filter states - default to "today" in configured timezone
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() => startOfDay(new Date()));
  const [dateTo, setDateTo] = useState<Date | undefined>(() => endOfDay(new Date()));
  const [selectedTech, setSelectedTech] = useState<string>('all');
  const [location, setLocation] = useState('all');
  // 'unpaid' = completed but not fully paid (the Awaiting payment card's rule)
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [displayLimit, setDisplayLimit] = useState(ITEMS_PER_PAGE);
  const [compareEnabled, setCompareEnabled] = useState(false);
  
  // Quick view state
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  
  const { data: techs = [] } = useTechs();
  const { data: metros = [] } = useMetros(true);

  // Reset pagination when filters or search change
  useEffect(() => {
    setDisplayLimit(ITEMS_PER_PAGE);
  }, [activeTab, dateFrom, dateTo, selectedTech, paymentFilter, location, effectiveSearch]);

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
  };

  // Fetch all jobs for real-time count updates (without date filter)
  const { data: allJobsData = [] } = useJobs('all', '', undefined, undefined);
  
  // When searching, ignore date filters to search across all jobs
  const isSearching = effectiveSearch.length > 0;

  // Fetch jobs for active tab with filters, search, and date range
  // When searching, pass undefined for dates to search all jobs and 'all' for status
  const { data: allJobs = [], isLoading } = useJobs(
    isSearching ? 'all' : TAB_TO_STATUS[activeTab],
    effectiveSearch,
    isSearching ? undefined : dateFrom,
    isSearching ? undefined : dateTo,
    timezone
  );

  // Apply filters to all jobs for accurate tab counts
  const applyFilters = (jobs: typeof allJobsData, status?: string) => {
    let filtered = jobs;

    // Filter by status if provided
    if (status) {
      filtered = filtered.filter(job => jobBoardStatus(job) === status);
    }

    // Apply date filters based on job status (a search spans all dates, so the counts must too)
    if ((dateFrom || dateTo) && !isSearching) {
      filtered = filtered.filter(job => {
        // Ongoing is live work: always shown, whatever the range
        if (jobBoardStatus(job) === 'in_progress') return true;

        // Use appropriate date field based on status
        let dateToCheck: string | null = null;
        
        if (job.status === 'completed') {
          // Completed jobs: use job_date, fall back to closed_at
          dateToCheck = job.job_date || job.closed_at;
        } else if (job.status === 'cancelled') {
          // Cancelled jobs: use job_date, fall back to cancelled_at
          dateToCheck = job.job_date || job.cancelled_at;
        } else if (job.status === 'scheduled') {
          // Fallback to created_at if scheduled_date is null
          dateToCheck = job.scheduled_date || job.created_at;
        } else {
          dateToCheck = job.created_at;
        }

        if (!dateToCheck) return false;

        // job_date is YYYY-MM-DD format, need to parse correctly
        const jobDate = new Date(dateToCheck.includes('T') ? dateToCheck : dateToCheck + 'T00:00:00');
        
        if (dateFrom && jobDate < dateFrom) return false;
        if (dateTo && jobDate > dateTo) return false;
        
        return true;
      });
    }

    if (selectedTech === 'unassigned') {
      filtered = filtered.filter(job => !job.assigned_tech_id);
    } else if (selectedTech === 'declined') {
      filtered = filtered.filter(isDeclinedByTech);
    } else if (selectedTech !== 'all') {
      filtered = filtered.filter(job => job.assigned_tech_id === selectedTech);
    }
    if (location && location !== 'all') {
      filtered = filtered.filter(job => job.metro_id === location);
    }
    if (paymentFilter === 'unpaid') {
      filtered = filtered.filter(job => job.status === 'completed' && job.payment_status !== 'paid');
    } else if (paymentFilter === 'paid') {
      filtered = filtered.filter(job => job.payment_status === 'paid');
    }

    return filtered;
  };

  const filteredAllJobs = useMemo(() => applyFilters(allJobsData), [allJobsData, dateFrom, dateTo, selectedTech, paymentFilter, location, isSearching]);

  // Calculate tab counts from filtered data
  const tabCounts = useMemo(() => ({
    ongoing: applyFilters(allJobsData, 'in_progress').length,
    scheduled: applyFilters(allJobsData, 'scheduled').length,
    on_hold: applyFilters(allJobsData, 'on_hold').length,
    quote_sent: applyFilters(allJobsData, 'quote_sent').length,
    completed: applyFilters(allJobsData, 'completed').length,
    cancelled: applyFilters(allJobsData, 'cancelled').length,
  }), [allJobsData, dateFrom, dateTo, selectedTech, paymentFilter, location, isSearching]);

  const filteredJobs = useMemo(() => {
    // When searching, skip all local filters - database already handled it
    if (isSearching) {
      return allJobs;
    }
    const rows = applyFilters(allJobs, TAB_TO_STATUS[activeTab]);
    // Upcoming reads as a queue: soonest appointment first, no-date jobs last.
    if (activeTab === 'scheduled') return [...rows].sort(byAppointment);
    return rows;
  }, [allJobs, activeTab, dateFrom, dateTo, selectedTech, paymentFilter, location, isSearching]);

  // Quick view navigation
  const { hasPrevious, hasNext, goToPrevious, goToNext } = useQuickViewNavigation({
    items: filteredJobs,
    getItemId: (job) => job.id,
    selectedId: selectedJobId,
    onSelect: setSelectedJobId,
  });

  const displayedJobs = useMemo(() => {
    const shouldPaginate = !isSearching;
    return shouldPaginate ? filteredJobs.slice(0, displayLimit) : filteredJobs;
  }, [filteredJobs, displayLimit, isSearching]);

  const remainingCount = filteredJobs.length - displayLimit;
  const hasMore = !isSearching && remainingCount > 0;

  const handleClearFilters = () => {
    setDateFrom(startOfDay(new Date()));
    setDateTo(endOfDay(new Date()));
    setSelectedTech('all');
    setPaymentFilter('all');
    setLocation('all');
    setSearchQuery('');
  };

  // The attention cards count across every job, so clicking one must drop the
  // date range and apply the same rule the card used — otherwise "11 unassigned"
  // lands on a today-only tab that shows nothing.
  const handleAttentionClick = (kind: JobsAttentionKind) => {
    setSearchQuery('');
    setPaymentFilter(kind === 'awaiting_payment' ? 'unpaid' : 'all');
    if (kind === 'unassigned') {
      setDateFrom(undefined);
      setDateTo(undefined);
      setSelectedTech('unassigned');
      // Land on the live tab holding the most unassigned jobs.
      const perTab: Record<JobTab, number> = { ongoing: 0, scheduled: 0, on_hold: 0, quote_sent: 0, completed: 0, cancelled: 0 };
      for (const job of allJobsData) {
        if (job.assigned_tech_id) continue;
        const tab = (Object.keys(TAB_TO_STATUS) as JobTab[]).find(t => TAB_TO_STATUS[t] === job.status);
        if (tab && tab !== 'completed' && tab !== 'cancelled') perTab[tab] += 1;
      }
      const liveTabs: JobTab[] = ['scheduled', 'ongoing', 'on_hold', 'quote_sent'];
      setActiveTab(liveTabs.reduce((best, tab) => (perTab[tab] > perTab[best] ? tab : best)));
    } else if (kind === 'tech_declined') {
      // A tech can only decline a scheduled job (tech_set_job_progress), so that tab holds them all.
      setDateFrom(undefined);
      setDateTo(undefined);
      setSelectedTech('declined');
      setActiveTab('scheduled');
    } else if (kind === 'overdue') {
      // Scheduled before today: everything up to the end of yesterday.
      setDateFrom(undefined);
      setDateTo(endOfDay(new Date(Date.now() - 24 * 60 * 60 * 1000)));
      setSelectedTech('all');
      setActiveTab('scheduled');
    } else {
      setDateFrom(undefined);
      setDateTo(undefined);
      setSelectedTech('all');
      setActiveTab('completed');
    }
  };

  const hasActiveFilters =
    dateFrom !== undefined ||
    dateTo !== undefined ||
    selectedTech !== 'all' ||
    paymentFilter !== 'all' ||
    (location !== '' && location !== 'all');

  const showMoreButton = hasMore ? (
    <div className="flex justify-center border-t py-5">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDisplayLimit(prev => prev + ITEMS_PER_PAGE)}
      >
        Show More ({remainingCount} remaining)
      </Button>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="space-y-5">
        <JobsHeader view="list" description="Every job by status. Upcoming is your queue, soonest first." />

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                placeholder="Search by customer, phone, or job number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-[280px] rounded-md pl-9 text-[13px]"
              />
            </div>

            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateChange={handleDateChange}
              compareEnabled={compareEnabled}
              onCompareChange={setCompareEnabled}
              align="end"
              className="h-9 min-w-0 rounded-md text-[13px] font-medium"
            />

            <Select value={selectedTech} onValueChange={setSelectedTech}>
              <SelectTrigger
                aria-label="Technician"
                className="h-9 w-auto min-w-[140px] gap-1.5 rounded-md text-[13px] font-medium"
              >
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="declined">Declined by tech</SelectItem>
                {techs.map(tech => (
                  <SelectItem key={tech.id} value={tech.id}>
                    {tech.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger
                aria-label="Location"
                className="h-9 w-auto min-w-[140px] gap-1.5 rounded-md text-[13px] font-medium"
              >
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {metros.map(metro => (
                  <SelectItem key={metro.id} value={metro.id}>
                    {metro.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={paymentFilter} onValueChange={(v) => setPaymentFilter(v as typeof paymentFilter)}>
              <SelectTrigger
                aria-label="Payment"
                className="h-9 w-auto min-w-[140px] gap-1.5 rounded-md text-[13px] font-medium"
              >
                <SelectValue placeholder="Any payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any payment</SelectItem>
                <SelectItem value="unpaid">Awaiting payment</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-9 rounded-md text-[13px] text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <AdminOnly>
          <JobsDashboard
            jobs={filteredAllJobs}
            allJobs={allJobsData}
            onAttentionClick={handleAttentionClick}
          />
        </AdminOnly>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as JobTab)}>
          <Card>
            <div className="overflow-x-auto border-b px-4">
              <TabsList className="-mb-px h-auto w-max justify-start gap-5 rounded-none bg-transparent p-0">
                {TAB_DEFS.map(tab => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="gap-2 rounded-none border-b-2 border-transparent bg-transparent px-1 py-3 text-[13px] font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    {tab.label}
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums',
                        activeTab === tab.value
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {tabCounts[tab.value]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value={activeTab} className="mt-0">
              {isLoading ? (
                <div className="py-16 text-center text-[13px] text-muted-foreground">Loading jobs...</div>
              ) : filteredJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <Briefcase className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">No jobs found</p>
                </div>
              ) : (
                <JobsTableView
                  jobs={displayedJobs}
                  showMoreButton={showMoreButton}
                  onJobClick={setSelectedJobId}
                  showCancelReason={activeTab === 'cancelled'}
                  groupByDay={activeTab === 'scheduled' && !isSearching ? { timezone, today: formatInTz(new Date(), timezone, 'yyyy-MM-dd') } : undefined}
                />
              )}
            </TabsContent>
          </Card>
        </Tabs>

        {/* Job Quick View Sheet */}
        <JobQuickViewSheet
          jobId={selectedJobId}
          open={!!selectedJobId}
          onOpenChange={(open) => !open && setSelectedJobId(null)}
          onPrevious={hasPrevious ? goToPrevious : undefined}
          onNext={hasNext ? goToNext : undefined}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      </div>
    </Layout>
  );
}
